import { injectable, inject } from '@servicetitan/react-ioc';
import { FeaturesApi, SavedPaymentMethodsApi } from 'api';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { getNormalizedFilter } from '../../../utils/settings';
import { PortalAuthStore } from '../../auth/stores/portal-auth.store';
import { getColorPaletteShadesByKey } from '../utils/color-palette-catalog';
import { SpecialSettingsStore } from './special-settings.store';

enum SpecialSettingsKeys {
    ProposalsEstimatesSettings = 'proposals-estimates-settings',
    ServiceAgreementsSettings = 'service-agreements-settings',
    MembershipSettings = 'membership-settings',
    DirectLinksSettings = 'direct-links-settings',
    EquipmentSettings = 'equipment-settings',
    JobFilterSettings = 'job-filter',
    InvoiceSettings = 'invoice-settings',
    JobSettings = 'job-settings',
    ColorPaletteSelection = 'color-palette-selection',
}

export interface ProposalsEstimatesSettings {
    enabled: boolean;
}

export interface ServiceAgreementsSettings {
    enabled: boolean;
}

export interface MembershipSettings {
    enabled: boolean;
}

export interface DirectLinksSettings {
    allowUserSignUpSignIn: boolean;
}

export interface PaymentMethodsSettings {
    enabled: boolean;
}

export interface EquipmentSettings {
    enabled: boolean;
}

export interface InvoiceSettings {
    enabled: boolean;
}

export interface JobSettings {
    enabled: boolean;
}

export interface JobsFilterSettings {
    scheduled: boolean;
    inProgress: boolean;
}
@injectable()
export class AppSettingsStore {
    @observable loadingSettings = false;
    @observable loaded = false;
    @observable proposalsEstimatesSettings: ProposalsEstimatesSettings = {
        enabled: false,
    };
    @observable serviceAgreementsSettings: ServiceAgreementsSettings = {
        enabled: false,
    };

    @observable membershipSettings: MembershipSettings = {
        enabled: false,
    };

    @observable directLinksSettings: DirectLinksSettings = {
        allowUserSignUpSignIn: true,
    };

    @observable paymentMethodsSettings: PaymentMethodsSettings = {
        enabled: false,
    };

    @observable equipmentSettings: EquipmentSettings = {
        enabled: true,
    };

    @observable invoiceSettings: InvoiceSettings = {
        enabled: true,
    };

    @observable jobSettings: JobSettings = {
        enabled: true,
    };

    @observable jobFilterSettings: JobsFilterSettings = {
        scheduled: true,
        inProgress: true,
    };

    /*
     * null = unset/no palette selected (today's uniform blue, matching the tenant-settings MFE's
     * own "Default" sentinel convention). Unwrapped from the { paletteKey } object shape stored
     * server-side (ARCH-SET-PALETTE-001 "Storage").
     */
    @observable colorPaletteSelection: string | null = null;

    private serviceAgreementsFGEnabled = false;
    private onlineEstimatesFGEnabled = false;
    private signableDocumentsFGEnabled = false;

    constructor(
        @inject(SpecialSettingsStore) private readonly specialSettings: SpecialSettingsStore,
        @inject(FeaturesApi) private featuresApi: FeaturesApi,
        @inject(SavedPaymentMethodsApi)
        private readonly savedPaymentMethodsApi: SavedPaymentMethodsApi,
        @inject(PortalAuthStore) private readonly portalAuthStore: PortalAuthStore
    ) {
        makeObservable(this);
        this.loadSettings();

        /*
         * Settings are first requested before login resolves, so unauthenticated
         * calls (e.g. features, saved payment methods) 403 and never retry. Reload
         * once auth completes so the store reflects the now-authenticated user.
         */
        reaction(
            () => this.portalAuthStore.authorized,
            authorized => {
                if (authorized) {
                    this.loadSettings();
                }
            }
        );
    }

    @computed get proposalsEstimatesEnabled() {
        return !!this.proposalsEstimatesSettings.enabled;
    }

    @computed get serviceAgreementsEnabled() {
        return !!this.serviceAgreementsSettings.enabled && this.serviceAgreementsFGEnabled;
    }

    @computed get onlineEstimatesEnabled() {
        return this.onlineEstimatesFGEnabled;
    }

    @computed get signableDocumentsEnabled() {
        return this.signableDocumentsFGEnabled;
    }

    @computed get membershipEnabled() {
        return !!this.membershipSettings.enabled;
    }

    @computed get directLinksSignUpSignInEnabled() {
        return !!this.directLinksSettings.allowUserSignUpSignIn;
    }

    @computed get paymentMethodsEnabled() {
        return !!this.paymentMethodsSettings.enabled;
    }

    @computed get equipmentEnabled() {
        return !!this.equipmentSettings.enabled;
    }

    @computed get invoiceEnabled() {
        return !!this.invoiceSettings.enabled;
    }

    @computed get jobEnabled() {
        return !!this.jobSettings.enabled;
    }

    /**
     * Resolved shades for the tenant's selected palette, or `undefined` when unset/unrecognized
     * — the signal consumers use to render with no theme override (today's default blue).
     */
    @computed get colorPaletteShades() {
        return getColorPaletteShadesByKey(this.colorPaletteSelection);
    }

    @action
    private async loadSettings() {
        this.loadingSettings = true;
        this.loaded = false;

        try {
            const [specialSettingsResult, features, savedPaymentMethodsConfiguration] =
                await Promise.allSettled([
                    this.specialSettings.load(),
                    this.featuresApi.getFeatureGates(),
                    this.savedPaymentMethodsApi.getConfiguration(),
                ]);

            runInAction(() => {
                if (specialSettingsResult.status === 'fulfilled') {
                    const dict = specialSettingsResult.value;
                    this.proposalsEstimatesSettings = {
                        ...this.proposalsEstimatesSettings,
                        ...getNormalizedFilter(
                            dict[SpecialSettingsKeys.ProposalsEstimatesSettings]
                        ),
                    };
                    this.serviceAgreementsSettings = {
                        ...this.serviceAgreementsSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.ServiceAgreementsSettings]),
                    };
                    this.membershipSettings = {
                        ...this.membershipSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.MembershipSettings]),
                    };
                    this.directLinksSettings = {
                        ...this.directLinksSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.DirectLinksSettings]),
                    };
                    this.equipmentSettings = {
                        ...this.equipmentSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.EquipmentSettings]),
                    };
                    this.jobFilterSettings = {
                        ...this.jobFilterSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.JobFilterSettings]),
                    };
                    this.invoiceSettings = {
                        ...this.invoiceSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.InvoiceSettings]),
                    };
                    this.jobSettings = {
                        ...this.jobSettings,
                        ...getNormalizedFilter(dict[SpecialSettingsKeys.JobSettings]),
                    };
                    const rawColorPaletteSelection = dict[
                        SpecialSettingsKeys.ColorPaletteSelection
                    ] as { paletteKey?: string | null } | undefined;
                    this.colorPaletteSelection =
                        typeof rawColorPaletteSelection?.paletteKey === 'string'
                            ? rawColorPaletteSelection.paletteKey
                            : null;
                }
                if (features.status === 'fulfilled') {
                    const fg = features.value.enabledFeatureGates;
                    this.serviceAgreementsFGEnabled = !!fg?.includes('ServiceAgreements');
                    this.onlineEstimatesFGEnabled = !!fg?.includes('EnableOnlineEstimates');
                    this.signableDocumentsFGEnabled = !!fg?.includes('SignableDocuments');
                }
                if (savedPaymentMethodsConfiguration.status === 'fulfilled') {
                    const {
                        isSavedAchFeatureEnabled,
                        isSavedCardFeatureEnabled,
                        isSavedPayByBankFeatureEnabled,
                    } = savedPaymentMethodsConfiguration.value;
                    this.paymentMethodsSettings = {
                        enabled:
                            isSavedAchFeatureEnabled ||
                            isSavedCardFeatureEnabled ||
                            isSavedPayByBankFeatureEnabled,
                    };
                }
            });
        } finally {
            runInAction(() => {
                this.loadingSettings = false;
                this.loaded = true;
            });
        }
    }
}
